import { useEffect } from 'react';
import type { ComponentProps } from 'react';
import { useRouter } from 'next/router';
import { QueryClientProvider } from '@tanstack/react-query';
import type { AppProps as NextAppProps } from 'next/app';

import '~/domains/market/utils/pusherFaker';

import FeatureFlagProvider from '~/components/providers/FeatureFlagProvider';
import { ThemeProvider } from '~/components/providers/ThemeProvider';
import UserProvider from '~/components/providers/UserProvider';
import AuthGuard from '~/components/providers/AuthGuard';
import ErrorBoundary from '~/components/shared/Errors/ErrorBoundary';
import ErrorBounadryScreen from '~/components/shared/Errors/ErrorBounadryScreen';
import IntercomLoggedIn from '~/components/Intercom/IntercomLoggedIn';
import queryClient from '~/config/initReactQuery';
import { reportPageView } from '~/domains/analytics/events';
import logVersionToConsole from '~/domains/common/utils/logVersionToConsole';
import type { NextPageComponent } from '~/domains/common/types/next-page-component.type';
import { MetaTags } from '~/domains/common/components/MetaTags/MetaTags';
import { RouteChangeOrMutationProgressBar } from '~/domains/app/components/RouteChangeOrMutationProgressBar/RouteChangeOrMutationProgressBar';
import { AppProviders } from '~/domains/common/app/AppProviders';
import { PageLayout } from '~/domains/common/app/PageLayout';
import useAnalytics from '~/domains/analytics';

logVersionToConsole();

type AppProps = NextAppProps & { Component: NextPageComponent };

function App({ Component, pageProps = {} }: Partial<AppProps>) {
  const router = useRouter();
  useAnalytics();

  const isGameshell = router.route.startsWith('/contest/[contestUuid]/game');
  const hasOwnAppshell = isGameshell || Component.hasOwnAppshell;
  const showErrorPage = router.route.startsWith('/error');
  const isPublicPage = Component.isPublic || false;
  // Override banner injection for pages that use Subheader component (otherwise it breaks layout)
  const hasAppBanners = Component.hasAppBanners ?? true;
  const pageName = Component.trackingName;
  const hideFooter = Component.hideFooter ?? false;
  const hideHeader = Component.hideHeader ?? false;
  const hideHeaderOnMobile = Component.hideHeaderOnMobile ?? false;
  const hideChromeInWebView = Component.hideChromeInWebView ?? false;
  // Resolved on the server by the marketplace pages' getServerSideProps so the first render already
  // reflects the webview; isWebView() then covers client-side navigations.
  const isWebViewInitial = pageProps.isWebView ?? false;
  const requiresAuth = !isPublicPage;
  const fullContest = pageProps.fullContest;
  const isDynamicContestLinkPreviewEnabled = pageProps.isDynamicContestLinkPreviewEnabled;
  const isDynamicContestLinkPreviewBase64Enabled =
    pageProps.isDynamicContestLinkPreviewBase64Enabled;
  const isDynamicContestLinkPreviewDeadlineEnabled =
    pageProps.isDynamicContestLinkPreviewDeadlineEnabled;
  const isReferAFriendEnabled = pageProps.isReferAFriendEnabled;
  const referralCode = router.query.referralCode as string;
  const handle = router.query.handle as string;

  const type = ((): ComponentProps<typeof MetaTags>['type'] => {
    if (referralCode && isReferAFriendEnabled) {
      return 'referral';
    }

    if (fullContest && isDynamicContestLinkPreviewEnabled) {
      return 'contest';
    }

    if (handle) {
      return 'channel';
    }

    return '';
  })();

  // Report page view on page load only for pages that have a tracking name
  useEffect(() => {
    if (pageName) {
      reportPageView(router.pathname, pageName);
    }
  }, [pageName]);

  if (showErrorPage) {
    return (
      <>
        <MetaTags type="" />
        <QueryClientProvider client={queryClient}>
          <UserProvider>
            <FeatureFlagProvider>
              <Component {...pageProps} />
            </FeatureFlagProvider>
          </UserProvider>
        </QueryClientProvider>
      </>
    );
  }

  return (
    <>
      <MetaTags
        fullContest={fullContest}
        type={type}
        referralCode={referralCode}
        handle={handle}
        isDynamicContestLinkPreviewEnabled={isDynamicContestLinkPreviewEnabled}
        isDynamicContestLinkPreviewBase64Enabled={isDynamicContestLinkPreviewBase64Enabled}
        isDynamicContestLinkPreviewDeadlineEnabled={isDynamicContestLinkPreviewDeadlineEnabled}
      />
      <ThemeProvider>
        <ErrorBoundary FallbackComponent={ErrorBounadryScreen}>
          <AppProviders>
            <IntercomLoggedIn />
            {requiresAuth ? (
              <AuthGuard>
                <PageLayout
                  hasOwnAppshell={hasOwnAppshell}
                  hasAppBanners={hasAppBanners}
                  hideFooter={hideFooter}
                  hideHeader={hideHeader}
                  hideHeaderOnMobile={hideHeaderOnMobile}
                  hideChromeInWebView={hideChromeInWebView}
                  isWebViewInitial={isWebViewInitial}
                >
                  <Component {...pageProps} />
                </PageLayout>
              </AuthGuard>
            ) : (
              <PageLayout
                hasOwnAppshell={hasOwnAppshell}
                hasAppBanners={hasAppBanners}
                hideFooter={hideFooter}
                hideHeader={hideHeader}
                hideHeaderOnMobile={hideHeaderOnMobile}
                hideChromeInWebView={hideChromeInWebView}
                isWebViewInitial={isWebViewInitial}
              >
                <Component {...pageProps} />
              </PageLayout>
            )}
            <RouteChangeOrMutationProgressBar />
          </AppProviders>
        </ErrorBoundary>
      </ThemeProvider>
    </>
  );
}

export default App;
